import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const empty = { title: "", excerpt: "", content: "", cover_image_url: "", published: true };

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/posts?all=true").then(({ data }) => setPosts(data));
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.created_at; delete payload.slug;
      if (editing === "new") {
        await api.post("/posts", payload);
        toast.success("Post added.");
      } else {
        await api.put(`/posts/${editing.id}`, payload);
        toast.success("Post updated.");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/posts/${p.id}`);
      toast.success("Post removed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <h1 className="font-fredoka text-4xl" style={{ color: "#2D3055" }}>Blog Posts</h1>
          <p className="mt-1" style={{ color: "#4A4E6D" }}>Small essays and notes from the writing nook.</p>
        </div>
        <button onClick={() => { setForm(empty); setEditing("new"); }} className="btn-terracotta" data-testid="add-post-btn">
          <Plus size={18} /> New post
        </button>
      </div>

      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="warm-card p-4 flex items-center gap-4" data-testid={`admin-post-${p.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-fredoka text-lg truncate" style={{ color: "#2D3055" }}>{p.title}</p>
              <p className="text-xs" style={{ color: "#7E82A1" }}>
                {p.published ? "Published" : "Draft"} • /{p.slug}
              </p>
            </div>
            <button onClick={() => { setForm({ ...empty, ...p }); setEditing(p); }} className="btn-ghost-warm text-xs" data-testid={`edit-post-${p.id}`}>
              <Pencil size={14} /> Edit
            </button>
            <button onClick={() => remove(p)} className="btn-ghost-warm text-xs" style={{ color: "#c1554b" }} data-testid={`delete-post-${p.id}`}>
              <Trash2 size={14} /> Delete
            </button>
          </div>
        ))}
        {posts.length === 0 && <p className="font-nunito" style={{ color: "#7E82A1" }}>No posts yet.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(61,64,91,0.5)" }} data-testid="post-modal">
          <div className="warm-card w-full max-w-2xl max-h-[90vh] overflow-auto p-8 relative">
            <button onClick={() => setEditing(null)} className="absolute top-4 right-4 p-2 rounded-full hover:bg-[#E7EEFF]" data-testid="post-modal-close">
              <X />
            </button>
            <h2 className="font-fredoka text-3xl mb-6" style={{ color: "#2D3055" }}>
              {editing === "new" ? "Write a new note" : "Edit note"}
            </h2>
            <form onSubmit={save} className="space-y-4">
              <div>
                <label className="font-fredoka text-sm block mb-1" style={{ color: "#2D3055" }}>Title</label>
                <input required value={form.title} onChange={upd("title")}
                  className="w-full rounded-2xl px-4 py-2.5 font-nunito focus:outline-none"
                  style={{ background: "#EEF2FF", border: "1.5px solid #A493E8", color: "#2D3055" }}
                  data-testid="post-field-title"
                />
              </div>
              <div>
                <label className="font-fredoka text-sm block mb-1" style={{ color: "#2D3055" }}>Cover image URL</label>
                <input type="url" value={form.cover_image_url} onChange={upd("cover_image_url")}
                  className="w-full rounded-2xl px-4 py-2.5 font-nunito focus:outline-none"
                  style={{ background: "#EEF2FF", border: "1.5px solid #A493E8", color: "#2D3055" }}
                  data-testid="post-field-cover"
                />
              </div>
              <div>
                <label className="font-fredoka text-sm block mb-1" style={{ color: "#2D3055" }}>Excerpt</label>
                <textarea required rows={2} value={form.excerpt} onChange={upd("excerpt")}
                  className="w-full rounded-2xl px-4 py-2.5 font-nunito focus:outline-none resize-none"
                  style={{ background: "#EEF2FF", border: "1.5px solid #A493E8", color: "#2D3055" }}
                  data-testid="post-field-excerpt"
                />
              </div>
              <div>
                <label className="font-fredoka text-sm block mb-1" style={{ color: "#2D3055" }}>Content (use blank lines for paragraphs)</label>
                <textarea required rows={10} value={form.content} onChange={upd("content")}
                  className="w-full rounded-2xl px-4 py-2.5 font-nunito focus:outline-none resize-none"
                  style={{ background: "#EEF2FF", border: "1.5px solid #A493E8", color: "#2D3055" }}
                  data-testid="post-field-content"
                />
              </div>
              <label className="flex items-center gap-2 font-fredoka text-sm" style={{ color: "#2D3055" }}>
                <input type="checkbox" checked={form.published} onChange={upd("published")} data-testid="post-field-published" />
                Published (visible on the site)
              </label>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className="btn-terracotta" data-testid="post-save-btn">
                  {saving ? "Saving…" : editing === "new" ? "Publish note" : "Save changes"}
                </button>
                <button type="button" onClick={() => setEditing(null)} className="btn-ghost-warm" data-testid="post-cancel-btn">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
